import { ComponentProps } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { AstraParagraph } from '~/astra'
import { selectStatusValues } from '~/store/slices/surgeonSchedulerFilterSlice'
import { selectPendingStatusesForCell } from '~/store/slices/surgeonSchedulerSlice'
import { useStore } from '~/store/store'
import { Location, Practitioner, PractitionerScheduleStatusCell, usePractitionerStatusDefinitions } from '~/storm'
import { getScheduleStatusesGap, pluralize } from '~/utils/utils'

import { type PractitionerStatusActivity } from '../../../PractitionerActivityDnd'
import { CellErrorHoverCard, EditInfoFooter, getDateBreadcrumb, getEditInfoText, getRoomNumber, PractitionerCellErrors } from '../../../shared'
import { ActivityTag } from './ActivityTag'

type StatusActivityTagProps = ComponentProps<typeof ActivityTag> & {
    cellErrors: PractitionerCellErrors | undefined
    title?: string | undefined
    editInfo?: string
    roomCodes?: string[]
}

const LocationNumberBadges = ({ roomCodes }: { roomCodes: string[] }) => (
    <span className="flex items-center gap-x-0.5 px-0.5">
        {roomCodes.map(roomCode => (
            <AstraParagraph className="rounded-md bg-white/30 px-0.5 text-body-small" key={roomCode}>
                {getRoomNumber(roomCode)}
            </AstraParagraph>
        ))}
    </span>
)

const StatusActivityTag = ({ cellErrors, editInfo, roomCodes, title, ...activityTagProps }: StatusActivityTagProps) => {
    const showLocationCodes = roomCodes && roomCodes.length > 0
    const displayTitle = showLocationCodes && title ? `${title} på ${pluralize(roomCodes.length, 'stue', 'r')} ${roomCodes.join(', ')}` : title
    if (cellErrors && cellErrors.hasErrors) {
        return (
            <CellErrorHoverCard
                breadcrumbs={[
                    getDateBreadcrumb(cellErrors.cell.scheduled_date),
                    ...(activityTagProps.label ? [{ label: cellErrors.practitioner?.short_name ?? '' }] : []),
                ]}
                triggerInfo={{ shortName: activityTagProps.label ?? '', label: displayTitle }}
                errors={Array.from(cellErrors.errorsByLocation.values()).flat()}
                footer={editInfo && <EditInfoFooter editInfo={editInfo} />}
                showErrorSources={true}
            >
                <ActivityTag
                    {...activityTagProps}
                    label={activityTagProps.label ?? ''}
                    colorType={'room_conflict'}
                    endContent={showLocationCodes ? <LocationNumberBadges roomCodes={roomCodes} /> : undefined}
                />
            </CellErrorHoverCard>
        )
    }
    const tooltip = [displayTitle, editInfo].filter(Boolean).join('\n')
    return (
        <ActivityTag
            {...activityTagProps}
            label={activityTagProps.label ?? ''}
            tooltip={tooltip || undefined}
            endContent={showLocationCodes ? <LocationNumberBadges roomCodes={roomCodes} /> : undefined}
        />
    )
}

type Props = {
    practitionerId: Practitioner['id']
    date: string
    toBeDropped: PractitionerStatusActivity | null
    cellErrors: PractitionerCellErrors | undefined
    statuses: PractitionerScheduleStatusCell[]
    cellLocations: Location[]
    showLocationBadges: boolean
    showPreview: boolean
}
export const Statuses = ({ practitionerId, date, toBeDropped, cellErrors, statuses, showPreview, cellLocations, showLocationBadges }: Props) => {
    const filteredStatuses = useStore(selectStatusValues)
    const filteredPendingStatuses = useStore(useShallow(selectPendingStatusesForCell(practitionerId, date)))
    const previewStatus = useStore(state => state.surgeonScheduler.recurringAssignmentApplyPreviewData.statuses).filter(
        status => status.date === date && status.practitionerId === practitionerId
    )
    const statusDefinitionsByCode = usePractitionerStatusDefinitions({ keyBy: 'code' })
    const previewStatusWithDefinition = showPreview
        ? previewStatus.map(status => ({ ...status, definition: statusDefinitionsByCode.get(status.definition.code) }))
        : []

    const pendingStatusesWithDefinition = filteredPendingStatuses.map(status => ({ ...status, definition: statusDefinitionsByCode.get(status.status_code) }))

    const allUniqueStatusDefinitions = new Map(
        [...statuses, ...pendingStatusesWithDefinition, ...previewStatusWithDefinition, toBeDropped].flatMap(st => {
            if (!st || !st.definition) return []
            return [[st.definition.uuid, st.definition]]
        })
    )

    const getLocationsForStatus = (statusCode: string) => {
        const availableLocations = [...cellLocations, ...(toBeDropped?.locations ?? [])]
        const statusDefinition = statusDefinitionsByCode.get(statusCode)
        if (statusDefinition?.assignment !== 'surgery') return []
        if (!statusDefinition.care) return availableLocations
        // a location only belongs to a status of the same care level,
        // but always belongs if its the only operating status
        const operatingStatuses = Array.from(allUniqueStatusDefinitions.values()).filter(status => status.assignment === 'surgery')
        return operatingStatuses.length > 1 ? availableLocations.filter(location => location.default_care_level === statusDefinition.care) : availableLocations
    }

    return (
        <div className={`cell flex items-stretch gap-x-0.5 ${getScheduleStatusesGap(allUniqueStatusDefinitions.size)}`}>
            {statuses.map(status => {
                const statusDefinition = status.definition
                const locations = getLocationsForStatus(statusDefinition?.code ?? '')
                const roomCodes = showLocationBadges ? locations.map(location => location.room_code) : undefined
                return (
                    <StatusActivityTag
                        dragItem={{ ...status, locations }}
                        key={status.uuid}
                        colorType={statusDefinition?.color ?? 'gray'}
                        isGrayedOut={filteredStatuses.length > 0 && !filteredStatuses.includes(statusDefinition?.code ?? '')}
                        editInfo={getEditInfoText(status)}
                        label={statusDefinition?.label}
                        title={statusDefinition?.title}
                        roomCodes={roomCodes}
                        cellErrors={cellErrors}
                    />
                )
            })}
            {showPreview &&
                previewStatus.map(status => {
                    const statusDefinition = status.definition
                    const roomCodes = showLocationBadges ? getLocationsForStatus(statusDefinition.code).map(location => location.room_code) : undefined
                    return (
                        <StatusActivityTag
                            key={statusDefinition.code}
                            colorType={statusDefinition.color}
                            isGrayedOut={filteredStatuses.length > 0 && !filteredStatuses.includes(statusDefinition.code)}
                            editInfo={getEditInfoText(status)}
                            label={statusDefinition.label}
                            title={statusDefinition.title}
                            roomCodes={showLocationBadges ? roomCodes : undefined}
                            cellErrors={cellErrors}
                        />
                    )
                })}
            {filteredPendingStatuses.map(status => {
                const roomCodes = showLocationBadges ? getLocationsForStatus(status.status_code).map(location => location.room_code) : undefined
                return (
                    <StatusActivityTag
                        key={status.status_code}
                        colorType={status.color ?? ''}
                        isGrayedOut={false}
                        label={status.label}
                        isLoading
                        roomCodes={roomCodes}
                        cellErrors={cellErrors}
                    />
                )
            })}
            {toBeDropped && !statuses.some(status => status.definition?.uuid === toBeDropped.definition?.uuid) && (
                <StatusActivityTag
                    key={toBeDropped.definition?.code}
                    colorType={toBeDropped.definition?.color ?? 'gray'}
                    label={toBeDropped.definition?.label ?? ''}
                    title={toBeDropped.definition?.title}
                    roomCodes={showLocationBadges ? getLocationsForStatus(toBeDropped.definition?.code ?? '').map(location => location.room_code) : undefined}
                    cellErrors={cellErrors}
                />
            )}
        </div>
    )
}
